import { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import { ConfirmationModal } from '@components/base/ConfirmationModal';

import { useBookmarkContext } from '@contexts/useBookmarkContext';
import { useBookmarks } from '@hooks/useBookmarks';
import useTranslation from '@hooks/useTranslation';
import { Delete, Edit, FolderOutlined, MoreVert } from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';

import { CreateNewFolderButton } from './CreateNewFolderButton';
import { EditBookmarkModal } from './EditBookmarkModal';
import { FolderButton } from './FolderButton';

export function SelectBookmarkFoldersController() {
    const router = useRouter();
    const { t } = useTranslation('bookmark');
    const { folders, setFolders, setHadiths } = useBookmarkContext();
    const { getStorageFolders, removeFolder } = useBookmarks();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedFolderUuid, setSelectedFolderUuid] = useState<string | null>(
        null,
    );
    const [openRemoveFolderModal, setOpenRemoveFolderModal] = useState(false);
    const [openEditFolderModal, setOpenEditFolderModal] = useState(false);

    function isFolderSelected(folderUuid: string) {
        return router.query.folder === folderUuid;
    }

    useEffect(() => {
        setSelectedFolderUuid(router.query.folder as string);
    }, [router.query.folder]);

    return (
        <>
            <Box
                sx={{
                    maxHeight: { xs: '100%', sm: '80svh' },
                    overflow: 'auto',
                    border: '1px solid #E6E6E6',
                    borderRadius: '5px',
                    position: { xs: 'static', sm: 'sticky' },
                    top: { xs: 0, sm: 10 },
                }}
            >
                <Box sx={{ mb: 2 }}>
                    <CreateNewFolderButton
                        onFolderCreated={async (folder) => {
                            router.push(`/bookmarks?folder=${folder.uuid}`);
                        }}
                    />
                </Box>
                {folders.map((folder) => (
                    <Box key={folder.uuid} className="truncate" sx={{ mb: 2 }}>
                        <FolderButton
                            onClick={async (e) => {
                                e.stopPropagation();
                                router.push(`/bookmarks?folder=${folder.uuid}`);
                            }}
                            contentSx={{
                                justifyContent: 'space-between',
                                gap: 2,
                            }}
                            sx={{
                                borderColor: isFolderSelected(folder.uuid)
                                    ? '#C7956D'
                                    : 'transparent',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    maxWidth: '75%',

                                    '@media (max-width: 600px)': {
                                        maxWidth: '80%',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <FolderOutlined />
                                </Box>
                                <Box
                                    sx={{
                                        width: '100%',
                                    }}
                                >
                                    <Typography
                                        color="#632626"
                                        component="p"
                                        className="truncate"
                                    >
                                        {folder.name}
                                    </Typography>

                                    <Typography variant="body2" color="#965D62">
                                        {folder.hadith_ids.length}{' '}
                                        {folder.hadith_ids.length === 1
                                            ? t('hadith')
                                            : t('hadiths')}
                                    </Typography>
                                </Box>
                            </Box>

                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();

                                    setSelectedFolderUuid(folder.uuid);
                                    setAnchorEl(e.currentTarget);
                                }}
                            >
                                <MoreVert />
                            </IconButton>
                        </FolderButton>
                    </Box>
                ))}
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                elevation={0}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        border: '1px solid #E6E6E6',
                        overflow: 'hidden',
                        borderRadius: '5px',
                        boxShadow: '0 0 10px rgba(238, 235, 235, 0.12)',
                    },
                }}
                onClose={(e: MouseEvent) => {
                    e.stopPropagation();
                    setAnchorEl(null);
                }}
            >
                <MenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        if (selectedFolderUuid) {
                            setOpenEditFolderModal(true);
                        }
                    }}
                    sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        color: '#632626',
                    }}
                >
                    <Edit sx={{ fontSize: '1rem' }} />
                    <Typography>{t('edit_folder')}</Typography>
                </MenuItem>
                <MenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpenRemoveFolderModal(true);
                    }}
                    sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        color: '#e72700',
                        '&:hover': {
                            bgcolor: '#fff2f0',
                        },
                    }}
                >
                    <Delete sx={{ fontSize: '1rem' }} />
                    <Typography>{t('delete_folder')}</Typography>
                </MenuItem>
            </Menu>
            {selectedFolderUuid && openRemoveFolderModal && (
                <ConfirmationModal
                    message={t('remove_folder_message')}
                    confirmButtonText={t('remove')}
                    handleClose={() => setOpenRemoveFolderModal(false)}
                    onConfirm={async () => {
                        await removeFolder(selectedFolderUuid);
                        setOpenRemoveFolderModal(false);
                        setSelectedFolderUuid(null);
                        setAnchorEl(null);
                        const folders = await getStorageFolders();
                        if (folders.length === 0) {
                            setFolders(folders);
                            setHadiths([]);
                            router.push('/bookmarks');
                        } else if (selectedFolderUuid === router.query.folder) {
                            router.push(`/bookmarks?folder=${folders[0].uuid}`);
                        } else {
                            setFolders(folders);
                        }
                    }}
                />
            )}
            {selectedFolderUuid && openEditFolderModal && (
                <EditBookmarkModal
                    folderUuid={selectedFolderUuid}
                    handleClose={async () => {
                        setFolders(await getStorageFolders()); // even if the user changed the folder details, the URL will still be the same, therefore we have to refetch the folders manually
                        setOpenEditFolderModal(false);
                        setSelectedFolderUuid(null);
                        setAnchorEl(null);
                    }}
                />
            )}
        </>
    );
}
